/**
 * Command-palette matching + recents — the pure, testable core of the
 * console's ⌘K palette (see ``components/CommandPalette``).
 *
 * Matching is SUBSEQUENCE, not substring: the query characters must appear
 * in order but not necessarily adjacently (``"apk"`` matches ``"AppKeys"``,
 * ``"plgd"`` matches ``"Playground"``). This is the Linear/VS Code feel and
 * is what ``cmdk`` is fed as its custom ``filter`` so the whole palette
 * scores identically. Empty query keeps every row (score 1) so the palette
 * shows its full menu (plus recents) on open.
 */

/**
 * True when every character of ``query`` appears in ``text`` in order
 * (case-insensitive). Whitespace in the query is ignored so ``"new key"``
 * still matches ``"Nuova AppKey"`` via ``n·e·w·k·e·y``.
 */
export function subsequenceMatch(text: string, query: string): boolean {
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase().replace(/\s+/g, "");
  if (needle.length === 0) return true;
  let i = 0;
  for (const char of haystack) {
    if (char === needle[i]) {
      i += 1;
      if (i === needle.length) return true;
    }
  }
  return false;
}

/**
 * ``cmdk`` filter adapter — returns ``1`` for a keep, ``0`` for a hide.
 * ``cmdk`` calls this per item with the item ``value`` and the current
 * ``search``; empty search keeps everything (the full menu is visible on
 * open). ``keywords`` (filename/aliases) widen the match surface.
 */
export function commandFilter(value: string, search: string, keywords?: string[]): number {
  if (search.trim().length === 0) return 1;
  const hay = keywords && keywords.length > 0 ? `${value} ${keywords.join(" ")}` : value;
  return subsequenceMatch(hay, search) ? 1 : 0;
}

// ── Recents ────────────────────────────────────────────────────────────────

/** Versioned like the other persisted console state (``ando.console-*.v1``). */
export const CONSOLE_RECENTS_STORAGE_KEY = "ando.console-recents.v1";

/** How many recent destinations the palette remembers. */
export const CONSOLE_RECENTS_CAP = 5;

/** Best-effort read of the persisted recents (most-recent-first). */
export function readConsoleRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(CONSOLE_RECENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string").slice(0, CONSOLE_RECENTS_CAP);
  } catch {
    return [];
  }
}

/**
 * Record a visited console path — dedupe, most-recent-first, capped. A no-op
 * on storage failure (private mode / Tauri edge). Returns the new list so
 * callers can update in-memory state without a re-read.
 */
export function pushConsoleRecent(path: string, previous?: string[]): string[] {
  const prior = previous ?? readConsoleRecents();
  const next = [path, ...prior.filter((p) => p !== path)].slice(0, CONSOLE_RECENTS_CAP);
  try {
    window.localStorage.setItem(CONSOLE_RECENTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Best-effort persistence only.
  }
  return next;
}
